import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { CLOVER_ALLOWED_USERS } from '../../utils/userUtils';

const SaveCardModal = ({ customerId, currentUsername, onClose, onCardSaved }) => {
  const [cardData, setCardData] = useState({ number: '', expMonth: '', expYear: '', cvv: '' });
  const [cardType, setCardType] = useState('credit');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [savedCard, setSavedCard] = useState(null);

  const isCloverEnabled = process.env.REACT_APP_CLOVER_ENABLED === 'true';
  const canSaveCard =
    isCloverEnabled &&
    process.env.REACT_APP_ENABLE_CARD_ON_FILE === 'true' &&
    CLOVER_ALLOWED_USERS.includes(currentUsername?.toLowerCase());

  if (!canSaveCard) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!cardData.number || !cardData.expMonth || !cardData.expYear || !cardData.cvv) {
      setError('Please fill in all card fields');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/api/clover/save-card-manual`, {
        customerId,
        cardData,
        username: currentUsername,
        isDebit: cardType === 'debit'
      });

      if (response.data.success) {
        setSavedCard(response.data.card);
        window.dispatchEvent(new CustomEvent('cardSavedForCustomer', { detail: { clientId: customerId } }));
        if (onCardSaved) onCardSaved(response.data.card);
      } else {
        setError(response.data.error || 'Failed to save card');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save card');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 10000 }}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '420px', width: '90%', backgroundColor: 'var(--dark-bg)', borderRadius: '8px', boxShadow: '0 8px 16px rgba(0,0,0,0.2)' }}
      >
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: '18px' }}>
            <i className="fas fa-credit-card" style={{ marginRight: '8px', color: '#007bff' }}></i>
            Save Card on File
          </h2>
          <button className="modal-close-btn" onClick={onClose}>&times;</button>
        </div>

        <div style={{ padding: '20px' }}>
          {savedCard ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#28a745', fontSize: '48px', marginBottom: '16px' }}>
                <i className="fas fa-check-circle"></i>
              </div>
              <h3 style={{ color: '#28a745', marginBottom: '8px' }}>
                {savedCard.isDuplicate ? 'Card Already on File' : 'Card Saved Successfully'}
              </h3>
              <p style={{ color: '#555' }}>
                {savedCard.brand} ending in {savedCard.last4} is saved to this customer's profile.
              </p>
              <p style={{ color: '#888', fontSize: '13px' }}>Expires {savedCard.expiry}</p>
              <button
                onClick={onClose}
                style={{
                  marginTop: '16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '10px 28px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSave}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px' }}>
                <i className="fas fa-lock" style={{ marginRight: '6px', color: '#28a745' }}></i>
                Card details are securely tokenized — never stored on DiNiTracker servers.
              </p>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '8px' }}>
                  Card Type <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {['credit', 'debit'].map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setCardType(type)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: `2px solid ${cardType === type ? '#007bff' : 'var(--border-color, #444)'}`,
                        backgroundColor: cardType === type ? 'rgba(0,123,255,0.1)' : 'transparent',
                        color: cardType === type ? '#007bff' : 'var(--text-secondary)',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: cardType === type ? '600' : '400',
                        transition: 'all 0.15s'
                      }}
                    >
                      <i className={`fas ${type === 'credit' ? 'fa-credit-card' : 'fa-money-check-alt'}`} style={{ marginRight: '6px' }}></i>
                      {type === 'credit' ? 'Credit Card' : 'Debit Card'}
                    </button>
                  ))}
                </div>
                {cardType === 'debit' && (
                  <p style={{ fontSize: '12px', color: '#28a745', marginTop: '6px', marginBottom: 0 }}>
                    <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                    Debit cards are not subject to the 3% processing fee.
                  </p>
                )}
              </div>

              {error && (
                <div style={{
                  backgroundColor: '#f8d7da',
                  border: '1px solid #f5c6cb',
                  color: '#721c24',
                  padding: '10px 14px',
                  borderRadius: '4px',
                  marginBottom: '16px',
                  fontSize: '13px'
                }}>
                  <i className="fas fa-exclamation-triangle" style={{ marginRight: '6px' }}></i>
                  {error}
                </div>
              )}

              <div style={{ display: 'grid', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                    Card Number
                  </label>
                  <input
                    type="text"
                    className="payment-input"
                    value={cardData.number}
                    onChange={(e) => setCardData({ ...cardData, number: e.target.value.replace(/\s/g, '') })}
                    placeholder="4111111111111111"
                    maxLength={16}
                    disabled={isProcessing}
                    style={{ width: '100%' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                      Exp Month
                    </label>
                    <input
                      type="text"
                      className="payment-input"
                      value={cardData.expMonth}
                      onChange={(e) => setCardData({ ...cardData, expMonth: e.target.value })}
                      placeholder="12"
                      maxLength={2}
                      disabled={isProcessing}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                      Exp Year
                    </label>
                    <input
                      type="text"
                      className="payment-input"
                      value={cardData.expYear}
                      onChange={(e) => setCardData({ ...cardData, expYear: e.target.value })}
                      placeholder="2027"
                      maxLength={4}
                      disabled={isProcessing}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                      CVV
                    </label>
                    <input
                      type="text"
                      className="payment-input"
                      value={cardData.cvv}
                      onChange={(e) => setCardData({ ...cardData, cvv: e.target.value })}
                      placeholder="123"
                      maxLength={4}
                      disabled={isProcessing}
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isProcessing}
                  style={{
                    flex: 1,
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '10px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isProcessing}
                  style={{
                    flex: 2,
                    backgroundColor: isProcessing ? '#6c757d' : '#007bff',
                    color: 'white',
                    border: 'none',
                    padding: '10px',
                    borderRadius: '6px',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {isProcessing ? (
                    <>
                      <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                      Saving Card...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save" style={{ marginRight: '8px' }}></i>
                      Save Card
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SaveCardModal;
